import { all, call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import i18n from '../../../i18n';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchVehicles,
  fetchVehiclesSuccess,
  fetchVehiclesFailure,
  fetchVehicleById,
  fetchVehicleByIdSuccess,
  fetchVehicleByIdFailure,
  addVehicle,
  addVehicleSuccess,
  addVehicleFailure,
  updateVehicle,
  updateVehicleSuccess,
  updateVehicleFailure,
  deleteVehicle,
  deleteVehicleSuccess,
  deleteVehicleFailure,
  bulkDeleteVehicles,
  bulkDeleteVehiclesSuccess,
  bulkDeleteVehiclesFailure,
} from './slice';

/**
 * Translate error message from backend
 * Maps common backend error messages to i18n translation keys
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('vehicles.errors.unknown');

  // Common error message mappings
  const errorMap = {
    'Vehicle already exists': 'vehicles.errors.vehicleExists',
    'License plate already exists': 'vehicles.errors.licensePlateExists',
    'Vehicle not found': 'vehicles.errors.vehicleNotFound',
    'Failed to add vehicle': 'vehicles.errors.addFailed',
    'Failed to update vehicle': 'vehicles.errors.updateFailed',
    'Failed to delete vehicle': 'vehicles.errors.deleteFailed',
    'Failed to fetch vehicles': 'vehicles.errors.fetchFailed',
    'Failed to fetch init data': 'vehicles.errors.initFailed',
  };

  // Check if error message matches a known pattern
  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  // If no mapping found, return original message
  return errorMessage;
}

// Worker saga: Fetch init data
function* handleFetchInit() {
  try {
    // Fetch units from init endpoint and vehicleTypes from vehicle-types endpoint in parallel
    const [initResponse, vehicleTypesResponse] = yield all([
      call(api.get, config.endpoints.vehicles.init),
      call(api.get, config.endpoints.vehicleTypes.list),
    ]);

    // Combine results: units from init, vehicleTypes from vehicle-types API
    const combinedResponse = {
      units: initResponse.units || [],
      vehicleTypes: vehicleTypesResponse.data || [],
    };

    yield put(fetchInitSuccess(combinedResponse));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchInitFailure(translatedError));
  }
}

// Worker saga: Fetch vehicles
function* handleFetchVehicles(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, filterName from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterName } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.search = filterName;
    }

    const response = yield call(api.get, config.endpoints.vehicles.list, params);
    // Pass the whole response (including pagination info)
    yield put(fetchVehiclesSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchVehiclesFailure(translatedError));
  }
}

// Worker saga: Fetch vehicle by ID
function* handleFetchVehicleById(action) {
  try {
    const vehicleId = action.payload;
    const response = yield call(api.get, `${config.endpoints.vehicles.list}/${vehicleId}`);
    yield put(fetchVehicleByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchVehicleByIdFailure(translatedError));
  }
}

// Worker saga: Add vehicle
function* handleAddVehicle(action) {
  try {
    const response = yield call(api.post, config.endpoints.vehicles.create, action.payload);
    yield put(addVehicleSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addVehicleFailure(translatedError));
  }
}

// Worker saga: Update vehicle
function* handleUpdateVehicle(action) {
  try {
    const { id } = action.payload;
    // Include id in the request body
    const response = yield call(api.put, `${config.endpoints.vehicles.update}/${id}`, action.payload);
    yield put(updateVehicleSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updateVehicleFailure(translatedError));
  }
}

// Worker saga: Delete vehicle
function* handleDeleteVehicle(action) {
  try {
    const vehicleId = action.payload;
    yield call(api.delete, `${config.endpoints.vehicles.delete}/${vehicleId}`);
    yield put(deleteVehicleSuccess(vehicleId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deleteVehicleFailure(translatedError));
  }
}

// Worker saga: Bulk delete vehicles
function* handleBulkDeleteVehicles(action) {
  try {
    const vehicleIds = action.payload;
    // Call API DELETE /vehicles with query params: ids=1&ids=2&ids=3
    yield call(api.delete, config.endpoints.vehicles.delete, { params: { ids: vehicleIds } });
    yield put(bulkDeleteVehiclesSuccess(vehicleIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeleteVehiclesFailure(translatedError));
  }
}

// Watcher saga
export function* watchVehiclesActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchVehicles.type, handleFetchVehicles);
  yield takeLatest(fetchVehicleById.type, handleFetchVehicleById);
  yield takeLatest(addVehicle.type, handleAddVehicle);
  yield takeLatest(updateVehicle.type, handleUpdateVehicle);
  yield takeLatest(deleteVehicle.type, handleDeleteVehicle);
  yield takeLatest(bulkDeleteVehicles.type, handleBulkDeleteVehicles);
}
